import { apiClient, restaurantOrderService } from '@kss/api';
import { autoAssignWaiter } from './waiterAssignment';
import type { CartLine, CustomerInfo, TableContext } from './types';

/**
 * Shared "place a dine-in order" routine, used by BOTH:
 *   - CustomerInfoSheet (first order — after Name/Mobile/OTP), and
 *   - CartSheet (repeat orders — customer already verified this session,
 *     so we skip the Customer Info sheet entirely).
 *
 * The dine place-order endpoint is anonymous (the qrToken is the auth),
 * so a returning customer never has to re-enter name/mobile/OTP. We pass
 * the already-known customer through and POST directly.
 *
 * Returns the created order id (string) or null on total failure.
 */
export async function placeDineOrder(opts: {
  qrToken: string;
  customer: CustomerInfo;
  lines: CartLine[];
  orderNote?: string;
  table?: TableContext | null;
}): Promise<string | null> {
  const { qrToken, customer, lines, orderNote, table } = opts;
  const subtotal = lines.reduce((s, l) => s + l.qty * l.price, 0);

  const itemRows = lines.map((l) => ({
    menuItemId: l.menuItemId,
    name: l.name,
    quantity: l.qty,
    unitPrice: l.price,
    taxRate: l.taxRate ?? 0,
    note: l.note ?? '',
  }));

  const orderPayload = {
    customer: {
      customerId: customer.customerId ?? '',
      name: customer.name ?? '',
      phone: customer.phone ?? '',
    },
    items: itemRows,    // what /dine/{qr}/place-order expects
    lines: itemRows,    // legacy alias for the CRUD fallback
    subtotal,
    orderNote: orderNote ?? '',
  };

  let orderId: string | null = null;
  try {
    const r = await apiClient.post(
      `/restaurant/dine/${encodeURIComponent(qrToken)}/place-order`,
      orderPayload,
    );
    orderId = String(r.data?.data?.id ?? r.data?.id ?? r.data?.orderId ?? '') || null;
  } catch {
    try {
      const r = await apiClient.post('/restaurant/RestaurantOrder/place-from-dine', {
        ...orderPayload,
        tableId: table?.tableId,
        tenantId: table?.tenantId,
        branchId: table?.branchId,
      });
      orderId = String(r.data?.data?.id ?? r.data?.id ?? r.data?.orderId ?? '') || null;
    } catch {
      try {
        const created = await restaurantOrderService.create([
          {
            name: `${table?.tableCode ?? 'Table'} - ${customer.name ?? 'Guest'}`,
            description: JSON.stringify(orderPayload).slice(0, 1900),
            status: 0,
            orderStatus: 'Placed',
            remarks: orderNote || undefined,
          } as never,
        ]);
        const row = created?.[0] as { id?: string | number } | undefined;
        orderId = row?.id != null ? String(row.id) : null;
      } catch {
        orderId = null;
      }
    }
  }

  if (orderId) void autoAssignWaiter(orderId);
  return orderId;
}
